import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import type { GridType } from "@/components/graph/types";
import type { DocumentModel } from "@/models/DocumentModel";

const GRID_TYPES: GridType[] = [0, 3, 1, 2, 4, 5];

type Props = {
  doc: DocumentModel;
  value: GridType;
  className?: string;
};

export const GraphGridType = observer(function GraphGridType({ doc, value, className }: Props) {
  const { tiptap } = doc.editor;
  const t = useTranslations("sidebar.activeNode");

  if (!tiptap) return null;

  const handleGridTypeChange = (gridType: GridType) => {
    tiptap.commands.updateAttributes("graph", { gridType });
    trackEvent("graph_grid_type_changed", { type: gridType });
  };

  const items = GRID_TYPES.map((level: GridType) => ({
    title: t(`graph.gridTypes.${level}`),
    active: value === level,
    icon: <Icon name={`graph/GridType.${level}`} />,
    onClick: () => handleGridTypeChange(level),
  }));

  const activeItem = items.find((item) => item.active);

  return (
    <Dropdown
      toggleButtonComponent={
        <Sidebar.ToggleButton
          as="div"
          icon={activeItem ? activeItem.icon : <Icon name="graph/GridType.0" />}
          title={activeItem ? activeItem.title : t("graph.gridTypes.0")}
          tooltipTitle={t("graph.actions.graphGridType")}
          className="min-h-[29px] w-full bg-input-bg shadow-block dark:shadow-block-inverted"
          wrapperClassName="py-0"
          contentClassName="truncate"
        />
      }
      items={items}
      className={twMerge("w-full text-left", className)}
      itemsClassName="min-w-[120px]"
    />
  );
});
